import { MOCK_AUDIT } from "@/mocks/audit";
import type { AuditActionType, AuditEntry, AuditQuery } from "@/services/types";
import { http } from "@/services/http";
import { USE_MOCK_API } from "@/services/config";

/**
 * Centralized audit service. Fetches immutable audit trail from database via REST API.
 */

const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));

const store: AuditEntry[] = [...MOCK_AUDIT];

export async function listAudit(query: AuditQuery = {}): Promise<AuditEntry[]> {
  if (!USE_MOCK_API) {
    try {
      const params = new URLSearchParams();
      if (query.action) params.set("action", query.action);
      if (query.module) params.set("module", query.module);
      if (query.search) params.set("search", query.search);
      const url = `/audit${params.toString() ? `?${params.toString()}` : ""}`;
      return await http<AuditEntry[]>(url);
    } catch {
      // Fallback
    }
  }
  await delay();
  let out = [...store];
  if (query.search) {
    const q = query.search.trim().toLowerCase();
    out = out.filter(
      (e) =>
        e.narration.toLowerCase().includes(q) ||
        e.entity.toLowerCase().includes(q) ||
        e.userName.toLowerCase().includes(q)
    );
  }
  if (query.action) out = out.filter((e) => e.action === query.action);
  if (query.module) out = out.filter((e) => e.module === query.module);
  if (query.user) out = out.filter((e) => e.userName === query.user);
  if (query.from) out = out.filter((e) => e.timestamp >= query.from!);
  if (query.to) out = out.filter((e) => e.timestamp <= query.to!);
  return out.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

export async function getAuditFacets(): Promise<{ users: string[]; actions: AuditActionType[]; modules: string[] }> {
  if (!USE_MOCK_API) {
    try {
      return await http<{ users: string[]; actions: AuditActionType[]; modules: string[] }>("/audit/facets");
    } catch {
      // Fallback
    }
  }
  await delay(80);
  const users = [...new Set(store.map((e) => e.userName))].sort();
  const actions = [...new Set(store.map((e) => e.action))].sort();
  const modules = [...new Set(store.map((e) => e.module))].sort();
  return { users, actions, modules };
}

export async function logAudit(entry: Omit<AuditEntry, "id" | "timestamp">): Promise<AuditEntry> {
  const full: AuditEntry = {
    ...entry,
    id: `a-${Date.now()}`,
    timestamp: new Date().toISOString(),
  };
  store.unshift(full);
  return full;
}

export const auditService = {
  listAudit,
  getAuditFacets,
  logAudit,
};